import request from '@/utils/request';
import { reqCatch } from '@/utils/catch';
import { fileToBlob, getLastTimeRepoId, filterApiParams } from '@/utils/index';
import type {
  RegisterReqType,
  UserProfileReqType,
  UserPreferenceReqType,
  UserRepoReqType,
  LoginMobile,
  UserProfileResType,
  UserProfile,
  OrgData,
  FollowerData,
  authListReqType,
  IBindUserInfo
} from './types';
import { reqCatchV2, type catchRt } from '@/utils/catch';
import type { AxiosResponse } from 'axios';
import type { ProfileRepoItemResData } from '@/views/User/hooks/useRepoList';
import { toLogin, toBindPhone, toChangeBindAccount } from './login';
import { getLoginTriggerSource, getRegisterSourceTab, getFullStepSource } from '@/constant/login';
import { AC_NEW_URL } from '@/constant/keys';
import { transformUrl } from '@/utils/tools';
/** 验证码登录 */
export function loginByMobile(data: Exclude<LoginMobile, 'type'>): Promise<any> {
  return reqCatch(() =>
    request({
      url: '/api/v1/user/oauth/login/mobile',
      method: 'post',
      data,
      headers: {
        'content-type': 'application/x-www-form-urlencoded',
        'X-Source': getLoginTriggerSource()
      }
    })
  );
}
/** 获取gitcode手机号验证码 */
export function verifyRegisterCode(data: {
  mobile: string;
  type: 'REGISTER' | 'ATTACH_MOBILE' | 'BINDING_MOBILE';
  raw_data?: string;
  captchaId?: string;
  token?: string;
  authenticate?: string;
  validate?: string;
}): Promise<any> {
  const { mobile, type, raw_data, captchaId, token, authenticate, validate } = data;
  const requestData = filterApiParams({
    biz_enum: type,
    mobile,
    raw_data: raw_data || '',
    captcha_id: captchaId,
    token,
    authenticate,
    validate
  });
  return reqCatch(() =>
    request({
      url: '/api/v1/user/sms/send/codeByBiz',
      method: 'post',
      data: requestData
    })
  );
}
/** 获取华为手机号验证码 */
export function verifyHwCode(data: { mobile: string }): Promise<any> {
  return reqCatch(() =>
    request({
      url: '/api/v1/user/sendVeriCode',
      method: 'post',
      data
    })
  );
}

/** gitcode手机号验证注册 */
export function registerByMobile(data: RegisterReqType): Promise<any> {
  return reqCatch(() =>
    request({
      url: '/api/v1/user/register',
      method: 'post',
      data,
      headers: {
        'last-repo-id': getLastTimeRepoId(),
        'X-Source': getFullStepSource()
      }
    })
  );
}

/**
 * 华为云手机号验证注册
 * @deprecated at 2023.11.02 精简注册
 */
export function registerBind(data: {
  mobile: string;
  user_id: string;
  mask: string;
  verificationcode: string;
}): Promise<any> {
  return reqCatch(() =>
    request({
      url: '/api/v1/user/registerIAM',
      method: 'post',
      data
    })
  );
}

/** 快捷注册短信验证 */
export function getQuickLoginMsg(data: {
  mobile: string;
  biz_enum?: string;
  captchaId?: string;
  token?: string;
  authenticate?: string;
  validate?: string;
}): Promise<any> {
  const { mobile, biz_enum, captchaId, token, authenticate, validate } = data;
  const requestData = filterApiParams({
    mobile,
    biz_enum: biz_enum || 'LOGIN',
    captcha_id: captchaId,
    token,
    authenticate,
    validate
  });
  // 过滤掉 requestData 中值为 undefined、null 或空字符串的字段
  return reqCatch(() =>
    request({
      url: '/api/v1/user/sms/send/codeByBiz',
      method: 'post',
      data: requestData
    })
  );
}

/** 手机号快捷注册 */
export function quickRegister(data: RegisterReqType): Promise<any> {
  return reqCatch(() =>
    request({
      url: '/api/v1/user/quickRegister',
      method: 'post',
      data: data
    })
  );
}

/** 重置密码 - 获取手机或者邮箱验证码 */
export function getMobileEmailCode({
  mobile_email = '',
  captcha_id = '',
  token = '',
  authenticate = '',
  validate = ''
}) {
  return reqCatch(() =>
    request({
      url: `/api/v1/user/forgetCode?mobile_email=${mobile_email}&captcha_id=${captcha_id}&token=${token}&authenticate=${authenticate}&validate=${validate}`
    })
  );
}

/** 登录重置密码 */
export function resetUserPassword(data: { mobile_email: string; password: string; code: string }) {
  return reqCatch(() =>
    request({
      url: '/api/v1/user/forgetPassword',
      method: 'post',
      data
    })
  );
}

/** 创建小程序二维码 */
export function createMiniProgramQRCode(data: { platform: string }) {
  return reqCatch(() =>
    request({
      url: `/uc/api/v1/qrcode/${data.platform}`,
      method: 'post',
      headers: {
        'X-Source': getLoginTriggerSource()
      }
    })
  );
}

/** 获取二维码状态 */
export function getMiniProgramQRCodeStatus(params: { platform: string; scene_id: string }): Promise<any> {
  const { platform, scene_id } = params;
  return reqCatch(
    () =>
      request({
        url: `/uc/api/v1/qrcode/${platform}`,
        method: 'get',
        params: { scene_id }
      }),
    params
  );
}

/** 二维码扫码认证，登录 */
export function miniProgramQRCodeLogin(params: { platform: string; scene_id: string }) {
  const { platform, scene_id } = params;
  return reqCatch(() =>
    request({
      url: `/api/v1/user/oauth/login/qrcode/${platform}`,
      method: 'post',
      params: { scene_id },
      headers: {
        'X-Source': getLoginTriggerSource()
      }
    })
  );
}

/** 退出登录 */
export function toLogout() {
  return reqCatch(() =>
    request({
      url: '/api/v1/user/oauth/logout',
      method: 'post'
    })
  );
}

/** 获取第三方绑定信息 */
export function getBindUserInfo(params: IBindUserInfo, returnUrl: string, csdnBlogSource: string) {
  const { platform } = params;
  return reqCatch(() =>
    request({
      url: `/uc/api/v2/oauth/callback/${platform}`,
      params,
      headers: {
        'last-repo-id': getLastTimeRepoId(),
        'X-Source': csdnBlogSource || getLoginTriggerSource() || getRegisterSourceTab(),
        'return-url': returnUrl
      }
    })
  );
}

/** 第三方登录绑定手机号 */
export function bindAuthMobile(data: {
  user_id: string;
  username: string;
  mask: string;
  identity_id: string;
  mobile: string;
  verificationcode: string;
  signup_type: string;
  platform: string;
}) {
  const params: {
    user_id: string;
    username: string;
    mask: string;
    identity_id: string;
    mobile: string;
    verificationcode: string;
    signup_type: string;
  } = {
    user_id: data.user_id,
    username: data.username,
    mask: data.mask,
    identity_id: data.identity_id,
    mobile: data.mobile,
    verificationcode: data.verificationcode,
    signup_type: data.signup_type
  };
  return reqCatch(() =>
    request({
      url: '/uc/api/v2/oauth/attach/mobile',
      method: 'post',
      data: params,
      headers: {
        'last-repo-id': getLastTimeRepoId(),
        'X-Source': getLoginTriggerSource() || getRegisterSourceTab()
      }
    })
  );
}

/** 绑定换绑操作或者直接登陆 **/
export function changeBindOrLogin(data: {
  identity_id: string;
  mask: string;
  action: 'BIND' | 'CHANGE_BIND' | 'JUST_LOGIN' | 'CHANGE_USER';
  platform: string;
}) {
  return reqCatch(() =>
    request({
      url: '/uc/api/v2/oauth/binding/confirm',
      method: 'post',
      data
    })
  );
}

/** 模糊查询用户信息 */
export function fuzzyFindUserInfo(usernameOrEmail: string) {
  return reqCatch(() =>
    request({
      url: '/api/v1/findUser/fuzzy',
      params: { usernameOrEmail }
    })
  );
}

// 邀请角色列表
export function invitRoles(params: { project_path: string; group_role: string; project_role: string }) {
  const { project_path, group_role, project_role } = params;
  return reqCatch(() =>
    request({
      url: `${AC_NEW_URL}/${project_path}/inviteRoleUser`,
      method: 'post',
      params: {
        group_role,
        project_role
      }
    })
  );
}

/** 查询用户名是否已存在 */
export function checkUsername(username: string) {
  return reqCatch(() =>
    request(
      {
        url: '/api/v1/user/checkSameUser',
        params: { username }
      },
      { customError: true }
    )
  );
}

// 获取当前用户所有命名空间
export function getNamespaces(params: any): Promise<any> {
  return reqCatch(
    () =>
      request({
        url: '/api/v1/groups',
        method: 'get',
        params
      }),
    params
  );
}

// 获取当前用户fork的所有命名空间
export function getForkGroups(params: any): Promise<any> {
  return reqCatch(
    () =>
      request({
        url: `/api/v2/projects/${params.repoId}/fork_groups/page`,
        method: 'get',
        params
      }),
    params
  );
}

// 获取当前用户所有命名空间
export function getManageableGroups(params: any): Promise<any> {
  return reqCatch(
    () =>
      request({
        url: '/api/v2/groups/user_groups/manageable_groups',
        method: 'get',
        params
      }),
    params
  );
}

// 获取当前用户所有命名空间（包含自己）
export function getManageableGroupsV2(params: any): Promise<any> {
  return reqCatch(
    () =>
      request({
        url: '/api/v2/groups/user_groups/manageable_namespace',
        method: 'get',
        params
      }),
    params
  );
}

export function postUploadAvatar(params: any): Promise<any> {
  return reqCatch(
    () =>
      request({
        url: '/api/v1/obs/user/avatar',
        method: 'get'
      }),
    params
  );
}

export function postUploadImage(params: any): Promise<any> {
  return reqCatch(
    () =>
      request({
        url: '/api/v1/obs/image',
        method: 'post',
        data: params
      }),
    params
  );
}

// 封装公共文件上传方法
export async function uploadFile(fileInfo: any, isAvatar: boolean, fileType: string = '') {
  try {
    if (fileInfo.length > 0) {
      const file = fileInfo[0];
      const params = {
        object_key: `${location.pathname}/` + file.name,
        file_type: file.name.split('.')[1],
        is_avatar: isAvatar
      };
      let res;
      if (isAvatar) {
        res = await postUploadAvatar(params);
      } else {
        params.content_type = fileType;
        res = await postUploadImage(params);
      }
      const blob: any = await fileToBlob(file);
      let url = '';

      let uploadParams: any = {};
      for (const key in res.data.data) {
        url = key;
        uploadParams = res.data.data[key];
      }
      const imgRes = await fetch(url, {
        method: 'put',
        body: blob,
        headers: {
          'Content-Type': uploadParams['Content-Type'],
          'x-obs-acl': uploadParams['x-obs-acl'],
          Host: uploadParams['Host']
        }
      });
      if (imgRes.status === 200) {
        return transformUrl(uploadParams['cdn-img-addr'] || '');
      } else {
        return '';
      }
    }
  } catch (err) {
    return err;
  }
}

export function findUser(data: object | null): Promise<object | null> {
  return request({
    url: '/api/v1/findUser/condition',
    method: 'get',
    params: data
  });
}
// 更新用户基本信息
export function updateUserProfile(
  data: UserProfileReqType | UserPreferenceReqType | UserRepoReqType
): catchRt<UserProfileResType> {
  return reqCatchV2(() =>
    request({
      url: '/api/v1/user/setting/save',
      method: 'post',
      data
    })
  );
}
// 查询用户信息
export function getUserProfile(params: { username: string; type?: string }): Promise<AxiosResponse<UserProfile>> {
  return request({
    url: '/api/v1/user/setting/profile',
    method: 'get',
    params: {
      ...params,
      username: params.username
    }
  });
}

// 查询用户活动-新接口
export function queryUserActivitiesNew(params: any): Promise<AxiosResponse<any>> {
  return request(
    {
      url: '/uc/api/v1/events/user',
      method: 'get',
      params
    },
    { customError: true }
  );
}

// 查询用户项目
export function queryUserRepos(params: {
  user_id: string;
  [propName: string]: any;
}): Promise<AxiosResponse<ProfileRepoItemResData[]>> {
  return request({
    url: `/api/v2/projects/featured/profile_project?group_id=${params.username}`,
    method: 'get',
    params
  });
}

// 查询用户创建的项目
export function queryUserCreateRepos(params: {
  user_name: string;
  search?: string;
  [propName: string]: any;
}): Promise<AxiosResponse<ProfileRepoItemResData[]>> {
  return request({
    url: `/api/v1/profile/${params.user_name}/created_projects`,
    method: 'get',
    params
  });
}

// 查询用户关注的项目
export function queryUserStarredRepos(params: {
  user_name: string;
  [propName: string]: any;
}): Promise<AxiosResponse<ProfileRepoItemResData[]>> {
  return request(
    {
      url: `/api/v1/profile/${params.user_name}/starred_projects`,
      method: 'get',
      params
    },
    { customError: true }
  );
}

// 更新项目状态
// export function updateRepoStatus(params: {
//   type: string;
//   group_id: string;
//   resource_id: string;
// }): Promise<AxiosResponse<any>> {
//   return request({
//     url: `/api/v2/projects/featured/${params.type}`,
//     method: 'post',
//     params
//   });
// }

// 批量更新精选项目
export function updateRepoStatus(data: {
  type: string;
  group_id: string;
  resource_id_list: string[];
}): Promise<AxiosResponse<any>> {
  return request({
    url: `/api/v2/projects/featured/${data.type}/batch/save`,
    method: 'post',
    data
  });
}

// 移除对应类型项目
export function removeRepoStatus(params: {
  type: string;
  group_id: string;
  resource_id: string;
}): Promise<AxiosResponse<any>> {
  return request({
    url: `/api/v2/projects/featured/${params.type}`,
    method: 'delete',
    params
  });
}

// 查询用户关注数
export function getUserFollows(params: { username: string }): Promise<AxiosResponse<number>> {
  return request({
    url: '/api/v1/follow/followingCount',
    method: 'get',
    params
  });
}

// 查询用户粉丝数
export function getUserFollowers(params: { username: string; followType: string }): Promise<AxiosResponse<number>> {
  return request({
    url: '/api/v1/follow/followersCount',
    method: 'get',
    params
  });
}

// 查询用户粉丝数和关注数
export function getUserFollowerCounts(params: {
  username: string;
}): Promise<AxiosResponse<{ follow_count: number; fans_count: number }>> {
  return request(
    {
      url: '/api/v1/follow/userBaseInfo',
      method: 'get',
      params
    },
    { customError: true }
  );
}

// 查询登录用户是否关注某用户
export function checkHasFollowed(params: {
  username: string;
  otherUsername: string;
  followType: number;
}): Promise<AxiosResponse<boolean>> {
  return request(
    {
      url: '/api/v1/follow/hasFollowed',
      method: 'get',
      params
    },
    {
      customError: true
    }
  );
}

// 查询特定用户组织列表
export function getUserOrgans(params: {
  user_name: string;
  page: number;
  per_page: number;
}): Promise<AxiosResponse<{ content: OrgData[] }>> {
  return request({
    url: `/api/v2/groups/${params.user_name}/list`,
    method: 'get',
    params
  });
}

// 查询特定用户的贡献图
export function queryUserContributes(params: {
  username: string;
  period?: string;
  year?: string;
}): Promise<AxiosResponse<{ [propName: string]: number }>> {
  return request(
    {
      url: `/uc/api/v1/events/${params.username}/contributions`,
      method: 'get',
      params
    },
    { customError: true }
  );
}

// 关注某用户
export function followUser(params: {
  username: string;
  followedUsername: string;
  followType: number;
}): Promise<AxiosResponse<any>> {
  return request({
    url: '/api/v1/follow',
    method: 'post',
    data: params
  });
}

// 取消关注某用户
export function unfollowUser(params: {
  username: string;
  unfollowUsername: string;
  followType: number;
}): Promise<AxiosResponse<any>> {
  return request({
    url: '/api/v1/follow',
    method: 'delete',
    params
  });
}

// 获取用户邮箱列表
export function getEmailList(params: { username: string; included_virtual?: boolean }): Promise<AxiosResponse<any>> {
  return request({
    url: '/api/v1/user/getEmailList',
    method: 'get',
    params
  });
}
export function getEmailListForCombobox(params: { username: string }): Promise<AxiosResponse<any>> {
  return request({
    url: '/api/v1/user/getEmailListForCombobox',
    method: 'get',
    params
  });
}

// 设置并更新邮箱
export function setVerifyEmail(params: { objectId: string }): Promise<AxiosResponse<any>> {
  return request({
    url: '/api/v1/user/verifyEmail',
    method: 'get',
    data: params
  });
}

// 删除用户设置邮箱
export function deleteEmail(params: { email: string }): Promise<AxiosResponse<any>> {
  return request({
    url: '/api/v1/user/deleteEmail',
    method: 'delete',
    params
  });
}

// 更新主邮箱设置
export function updateEmail(params: { username: string; email: string }): Promise<AxiosResponse<any>> {
  return request({
    url: '/api/v1/user/updateEmail',
    method: 'put',
    data: params
  });
}

// 发送更新邮箱验证码接口
export function sendEmailVeriCode(params: { username: string; email: string }): Promise<AxiosResponse<any>> {
  return request({
    url: '/api/v1/user/sendEmailVeriCode',
    method: 'post',
    data: params
  });
}

// 发送更新邮箱验证码接口
export function verifyCodeEmail(params: { code: string; email: string }): Promise<AxiosResponse<any>> {
  return request({
    url: '/api/v1/user/verifyCodeEmail',
    method: 'put',
    params: params
  });
}

// 设置密码
export function setPassword(params: { password: string; username: string }): Promise<AxiosResponse<any>> {
  return request({
    url: '/api/v1/user/setPassword',
    method: 'post',
    data: params
  });
}

// 修改密码
export function updatePassword(params: {
  origin_password: string;
  password: string;
  username: string;
}): Promise<AxiosResponse<any>> {
  return request({
    url: '/api/v1/user/updatePassword',
    method: 'post',
    data: params
  });
}

// 判断是否有设置密码
export function passwordAvailable(): Promise<AxiosResponse<any>> {
  return request({
    url: '/api/v1/user/passwordAvailable',
    method: 'get'
  });
}

// 获取当前用户加入的组织
export function getMyGroupsList(params: any): Promise<AxiosResponse<any>> {
  return request({
    url: '/api/v2/groups/user_groups/related_me',
    method: 'get',
    params
  });
}
// 获取企业下的组织列表
export function getOrgList(params: {
  enterpriseId: string;
  search?: string;
  page?: number;
  per_page?: number;
}): Promise<any> {
  return request({
    url: `/api/v2/groups/enterprise/${params.enterpriseId}/group_list`,
    method: 'get',
    params
  });
}
/** 退出组织 */
export function leaveGroups(params: any): Promise<AxiosResponse<any>> {
  return request({
    url: `/api/v1/groups/${params.orgId}/leave`,
    method: 'delete'
  });
}

// 获取用户访问令牌列表
export function getImpersonationTokens(params: any): Promise<AxiosResponse<any>> {
  return request({
    url: `/api/v1/user/getImpersonationTokens`,
    method: 'get',
    params
  });
}

// 创建用户访问令牌
export function addImpersonationTokens(params: any): Promise<AxiosResponse<any>> {
  return request({
    url: `/api/v1/user/${params.username}/impersonation_tokens`,
    method: 'post',
    data: params
  });
}
// 删除用户访问令牌
export function deleteImpersonationTokens(params: any): Promise<AxiosResponse<any>> {
  return request({
    url: `/api/v1/user/${params.username}/impersonation_tokens/${params.id}`,
    method: 'delete',
    params: { name: params.name }
  });
}
// 活动会话列表
export function getLatest(): Promise<AxiosResponse<any>> {
  return request({
    url: `/api/v1/user/activity-session/latest`,
    method: 'get'
  });
}
// 删除活动会话
export function deleteOffline(params: any): Promise<AxiosResponse<any>> {
  return request({
    url: `/api/v1/user/activity-session/offline`,
    method: 'put',
    params
  });
}

// 获取用户参与的项目
export function getUserProject(params: any): Promise<AxiosResponse<any>> {
  return request({
    url: `/api/v2/projects/user_projects/related_me`,
    method: 'get',
    params
  });
}

// 查询用户有维护者及以上权限的模板仓
export function getValidTplRepoList(params: any): Promise<AxiosResponse<any>> {
  return request({
    url: `/api/v2/projects/user/projects`,
    method: 'get',
    params
  });
}

// 获取用户粉丝列表
export function getFollowersList(params: {
  username: string;
  pageNum: number;
  pageSize: number;
}): Promise<AxiosResponse<{ content: FollowerData[]; total: number }>> {
  return request(
    {
      url: '/api/v1/follow/followers',
      method: 'get',
      params
    },
    { customError: true }
  );
}
// 搜索用户列表
export function searchUserList(params: {
  keyword?: string;
  pageNum: number;
  pageSize: number;
}): Promise<AxiosResponse<{ content: any[]; total: number }>> {
  return request(
    {
      url: '/api/v1/user/search',
      method: 'get',
      params
    },
    { customError: true }
  );
}
// 获取用户关注列表
export function getFollowingList(params: {
  username: string;
  pageNum: number;
  pageSize: number;
}): Promise<AxiosResponse<{ content: FollowerData[]; total: number }>> {
  return request(
    {
      url: '/api/v1/follow/followingList',
      method: 'get',
      params
    },
    { customError: true }
  );
}

// 获取用户语言集合
export function getUserLangData(params: { type: string; user_name: string }): Promise<AxiosResponse<string[]>> {
  return request(
    {
      url: `/api/v1/profile/${params.user_name}/project_languages`,
      method: 'get',
      params
    },
    { customError: true }
  );
}

// 获取用户关注的项目动态推送
export function getUserConcernEvents(params?: any): catchRt<any> {
  return reqCatchV2(() =>
    request({
      url: '/uc/api/v1/events/follower_events',
      method: 'get',
      params
    })
  );
}

// 获取用户被邀请的项目列表
export function getUserInviteRepo(params?: any): Promise<AxiosResponse<string[]>> {
  return request(
    {
      url: `/api/v1/invite/sourcePage`,
      method: 'get',
      params
    },
    { customError: true }
  );
}

// 获取用户绑定的三方信息
export function getUserIdentity(params?: any): catchRt<any> {
  return reqCatchV2(() =>
    request({
      url: '/api/v1/user/identity/list',
      method: 'get',
      params
    })
  );
}

// 用户绑定三方平台
export function bindUserIdentity(data?: any): catchRt<any> {
  return reqCatchV2(() =>
    request({
      url: '/api/v1/user/identity',
      method: 'post',
      data
    })
  );
}

// 第三方平台解绑
export function unBindUserIdentity(data?: any): catchRt<any> {
  return reqCatchV2(() =>
    request({
      url: '/api/v1/user/identity',
      method: 'delete',
      data
    })
  );
}

// 获取当前用户的手机号
export function getLoginMobile(params?: any): catchRt<any> {
  return reqCatchV2(() =>
    request({
      url: '/api/v1/user/mobile',
      method: 'get',
      params
    })
  );
}

// 用户账号注销
export function userAccountDel(params?: any): catchRt<any> {
  return reqCatchV2(() =>
    request({
      url: '/api/v1/user/del',
      method: 'delete',
      params
    })
  );
}

// 获取第三方atomgit账号使用流水线
export function getAtomgitIdentity(): catchRt<any> {
  return reqCatchV2(() =>
    request({
      url: '/api/v1/user/identity/atomgit',
      method: 'get'
    })
  );
}

// 获取登录弹窗配置
export function getLoginConfig(): catchRt<any> {
  return reqCatchV2(() =>
    request({
      url: '/api/v1/config/isLogin',
      method: 'get'
    })
  );
}

// 获取登陆token
export function getUserToken(): catchRt<any> {
  return reqCatchV2(() =>
    request({
      url: '/api/v1/user/oauth/token',
      method: 'get'
    })
  );
}
// 获取用户信息
export function getUserInfo(): catchRt<any> {
  return reqCatchV2(() =>
    request(
      {
        url: '/api/v1/user/oauth/userInfo',
        method: 'get'
      },
      {
        customError: true
      }
    )
  );
}
// 获取用户在组织或项目中拥有的权限列表
export function getUserAuthList(params: authListReqType): Promise<any> {
  return reqCatch(() =>
    request({
      url: '/api/v2/ac/auth/list',
      method: 'get',
      params
    })
  );
}
// 获取聚合权限数据（权限列表和access_level信息）
export function getUserAuth(params: { path: string }): Promise<any> {
  return request(
    {
      url: '/api/v2/ac/auth',
      method: 'get',
      params
    },
    {
      customError: true
    }
  );
}
// 获取第三方信息
export function getAuthorizeInfo(clientId: string, redirect_uri: string): catchRt<any> {
  return reqCatchV2(() =>
    request(
      {
        url: `/api/v1/oauth/client/${clientId}`,
        method: 'get',
        params: {
          redirect_uri
        }
      },
      {
        customError: true
      }
    )
  );
}
export function getReadmeFile(params: any): Promise<any> {
  return reqCatch(() =>
    request({
      url: `/api/v2/readme/${params.username}/readme/file`,
      method: 'get'
    })
  );
}

/** 获取配置的图片验证码类型 */
export function getCaptchaType(): Promise<any> {
  return reqCatch(() =>
    request({
      url: `/uc/api/v1/captcha/config`,
      method: 'get'
    })
  );
}

// mfa 验证码登陆
export function postMfaLogin(data: any): catchRt<any> {
  return reqCatchV2(() =>
    request({
      url: '/api/v1/user/oauth/login/mfa',
      method: 'post',
      data,
      headers: {
        'content-type': 'application/x-www-form-urlencoded',
        'X-Source': getLoginTriggerSource()
      }
    })
  );
}

// mfa 恢复码登陆
export function postMfaRecover(data: any): catchRt<any> {
  return reqCatchV2(() =>
    request({
      url: '/api/v1/user/oauth/login/mfa_recover',
      method: 'post',
      data,
      headers: {
        'content-type': 'application/x-www-form-urlencoded',
        'X-Source': getLoginTriggerSource()
      }
    })
  );
}

// 判断是否授权过
export function checkOrAuthorize(data: any): catchRt<any> {
  return reqCatchV2(() =>
    request({
      url: '/api/v1/oauth/checkOrAuthorize',
      method: 'post',
      data,
      headers: {
        'content-type': 'application/x-www-form-urlencoded'
      }
    })
  );
}

// 我的成就
export const getMedalRelatedMe = (params): catchRt<any> => {
  return reqCatchV2(() =>
    request({
      url: '/uc/api/v1/medal/related_me',
      method: 'get',
      params
    })
  );
};

// 获取用户迁移信息
export const getOhDeveloperInfo = (params?: any): catchRt<any> => {
  return reqCatchV2(() =>
    request({
      url: '/api/v1/user/oh-developer-info',
      method: 'get',
      params
    })
  );
};

// 判断当前用户是否有 项目 issue 和 pr 信息
export const getProjectIssuePrInfo = () => {
  return reqCatchV2(() =>
    request({
      url: '/api/v2/projects/profile/project_issue_pr_info',
      method: 'get'
    })
  );
};

// 获取问卷列表
export function getRegionList(params: any = {}): Promise<any> {
  return request({
    url: '/uc/api/v1/dicts/by-keys',
    method: 'get',
    params
  });
}
// 提交问卷
export function submitUserTagQuestions(data: any): Promise<any> {
  return request({
    url: '/api/v1/user/setting/save',
    method: 'post',
    data
  });
}
export { toLogin, toBindPhone, toChangeBindAccount };

/** 判断邮箱是否被绑定 */
export function emailIsBinding(params: any = {}): Promise<any> {
  return reqCatch(
    () =>
      request({
        url: '/api/v1/user/email/check-valid',
        method: 'get',
        params
      }),
    params
  );
}

/** 验证邮箱是否真实可用 */
export function getEmailValid(params: any = {}): Promise<any> {
  return reqCatch(
    () =>
      request({
        url: '/api/v1/user/setEmailValid',
        method: 'get',
        params
      }),
    params
  );
}

/** 获取邮箱验证码 */
export function getEmailCode(params: any): Promise<any> {
  return reqCatch(
    () =>
      request({
        url: '/api/v1/user/email/send/codeByBiz',
        method: 'post',
        data: params
      }),
    params
  );
}

/** 原手机号或邮箱验证 */
export function mobileOrEmailVerify(params: any): Promise<any> {
  return reqCatch(
    () =>
      request({
        url: '/api/v1/user/gen-mask',
        method: 'get',
        params
      }),
    params
  );
}

/** 欧拉 Guide 页，获取当前用户迁移状态 */
export function getUserMigrateStatus(params: { import_batch_number: number }): Promise<any> {
  return reqCatch(
    () =>
      request({
        url: '/api/v1/user/migrate-status',
        method: 'get',
        params
      }),
    params
  );
}

/**
 * 我的项目列表
 */
export function queryUserReposList(params: {
  user_name: string;
  /** 搜索关键词 */
  search?: string;
  order_by?: 'id' | 'name' | 'path' | 'created_at' | 'updated_at' | 'last_activity_at';
  sort?: 'asc' | 'desc';
  visibility?: 'private' | 'internal' | 'public';
  /** 仓库类型 0 代码仓 1 模型仓 2 数据集仓 3 space仓 */
  repo_type?: string;
  /** 仓库查询类型 */
  repo_query_type?: 'starred' | 'created' | 'owned';
  page?: number;
  per_page?: number;
  namespace_id?: number; // 根据组织id查询
  view: 'simple' | 'all';
  archived?: boolean;
}): Promise<
  AxiosResponse<{
    content: ProfileRepoItemResData[];
    page_count: number;
    page_num: number;
    page_size: number;
    total: number;
  }>
> {
  return request({
    url: `/api/v2/projects/profile/${params.user_name}/projects`,
    method: 'get',
    params
  });
}



export function getRepoList(params: any): Promise<AxiosResponse<any>> {
  return request({
    url: `/api/v2/projects`,
    method: 'get',
    params
  });
}
